import { useEffect, useRef, useState } from "react";
import { EncryptionProvider } from "common/crypto";
import masterKeyValidationAdapter from "common/Adapters/MasterKeyValidationAdapter/Router";

const MIN_KEY_LENGTH = 5;
const MAX_KEY_LENGTH = 15;
const OPERATION = { CREATE: "create", CHANGE: "change", RESET: "reset" };
const isValidLength = (key) => key.length >= MIN_KEY_LENGTH && key.length <= MAX_KEY_LENGTH;

export default function useMasterKeyWorkflow({
  translate,
  initialMode = "enter-key",
  resetOutcome = "authenticated",
  onAuthenticated,
  onWithoutKey,
  onClose,
}) {
  const [masterKeyMode, setMasterKeyMode] = useState(initialMode);
  const [key, setKey] = useState("");
  const [oldKey, setOldKey] = useState("");
  const [pendingOperation, setPendingOperation] = useState(null);
  const [invalidText, setInvalidText] = useState("");
  const [invalidTextOldKey, setInvalidTextOldKey] = useState("");
  const submittingRef = useRef(false);
  const preparedChangeRef = useRef(null);
  const mountedRef = useRef(true);
  const flowVersionRef = useRef(0);

  const keyLengthError = () => translate("benchmarking.master-key-modal.invalid-text.key-length");
  const wrongKeyError = () => translate("benchmarking.master-key-modal.invalid-text.change-master-key.wrong-old-key");
  const clearErrors = () => {
    setInvalidText("");
    setInvalidTextOldKey("");
  };
  const cancelPreparedChange = () => {
    preparedChangeRef.current?.cancel?.();
    preparedChangeRef.current = null;
  };
  const invalidateFlow = () => {
    flowVersionRef.current += 1;
    cancelPreparedChange();
  };
  const isCurrentFlow = (version) => mountedRef.current && flowVersionRef.current === version;
  const wasFlowExplicitlyInvalidated = (version) => flowVersionRef.current !== version;
  const discardStaleActivatedSession = (response) => {
    const activatedRevision = response?.data?.cryptoSessionRevision;
    if (Number.isInteger(activatedRevision)) EncryptionProvider.purgeIfRevision(activatedRevision);
  };
  const resetInputs = (mode = initialMode) => {
    invalidateFlow();
    setKey("");
    setOldKey("");
    setPendingOperation(null);
    clearErrors();
    setMasterKeyMode(mode);
  };
  const finish = (callback) => {
    if (!mountedRef.current) return;
    resetInputs();
    callback?.();
  };
  const finishWithKey = () => finish(onAuthenticated);
  const finishWithoutKey = () => {
    if (EncryptionProvider.isInitialized() || EncryptionProvider.hasStoredSession()) EncryptionProvider.purge();
    finish(onWithoutKey);
  };
  const startConfirmation = (operation) => {
    setPendingOperation(operation);
    setMasterKeyMode("confirm-new-key");
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      cancelPreparedChange();
    };
  }, []);

  const submitEnter = async () => {
    if (!isValidLength(key)) {
      setInvalidText(keyLengthError());
      return;
    }
    const flowVersion = flowVersionRef.current;
    const response = await masterKeyValidationAdapter.isValid(key);
    if (!isCurrentFlow(flowVersion)) {
      if (wasFlowExplicitlyInvalidated(flowVersion)) discardStaleActivatedSession(response);
      return;
    }
    if (!response?.data?.valid) {
      setInvalidText(wrongKeyError());
      return;
    }
    finishWithKey();
  };

  const submitChange = async () => {
    const oldValid = isValidLength(oldKey);
    const newValid = isValidLength(key);
    if (!oldValid || !newValid) {
      if (!oldValid) setInvalidTextOldKey(keyLengthError());
      if (!newValid) setInvalidText(keyLengthError());
      return;
    }
    if (oldKey === key) {
      const message = translate("benchmarking.master-key-modal.invalid-text.change-master-key.same-key");
      setInvalidTextOldKey(message);
      setInvalidText(message);
      return;
    }
    cancelPreparedChange();
    const flowVersion = flowVersionRef.current;
    const prepared = await masterKeyValidationAdapter.prepareChange(oldKey, key);
    if (!isCurrentFlow(flowVersion)) {
      prepared.cancel?.();
      return;
    }
    if (!prepared.valid) {
      setInvalidTextOldKey(wrongKeyError());
      return;
    }
    preparedChangeRef.current = prepared;
    setOldKey("");
    startConfirmation(OPERATION.CHANGE);
  };

  const submitConfirmation = async () => {
    if (!isValidLength(key)) {
      setInvalidText(keyLengthError());
      return;
    }
    const flowVersion = flowVersionRef.current;
    let response;

    if (pendingOperation === OPERATION.CREATE) {
      response = await masterKeyValidationAdapter.add(key);
    } else if (pendingOperation === OPERATION.CHANGE) {
      const prepared = preparedChangeRef.current;
      preparedChangeRef.current = null;
      if (!prepared) throw new Error("The prepared master-key change is no longer available");
      response = await prepared.confirm();
    } else if (pendingOperation === OPERATION.RESET)
      response = await masterKeyValidationAdapter.reset(key, { activateSession: resetOutcome !== "generic" });
    else throw new Error("Cannot confirm master key without a valid operation");

    if (!isCurrentFlow(flowVersion)) {
      if (wasFlowExplicitlyInvalidated(flowVersion) && !(pendingOperation === OPERATION.RESET && resetOutcome === "generic"))
        discardStaleActivatedSession(response);
      return;
    }
    if (pendingOperation === OPERATION.RESET && resetOutcome === "generic") finishWithoutKey();
    else finishWithKey();
  };

  const submit = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    const flowVersion = flowVersionRef.current;
    clearErrors();
    try {
      if (masterKeyMode === "enter-key") await submitEnter();
      else if (masterKeyMode === "create-key") {
        if (isValidLength(key)) startConfirmation(OPERATION.CREATE);
        else setInvalidText(keyLengthError());
      } else if (masterKeyMode === "change-key") await submitChange();
      else if (masterKeyMode === "reset-key") {
        if (isValidLength(key)) startConfirmation(OPERATION.RESET);
        else setInvalidText(keyLengthError());
      } else if (masterKeyMode === "confirm-new-key") await submitConfirmation();
      else throw new Error(`Unsupported master key mode: ${masterKeyMode}`);
    } catch (error) {
      console.error("Error during master key submission", error);
      if (error?.requiresRecovery && (EncryptionProvider.isInitialized() || EncryptionProvider.hasStoredSession())) {
        EncryptionProvider.purge();
      }
      if (!isCurrentFlow(flowVersion)) return;
      const message = error?.message || "Master-key operation failed.";
      if (masterKeyMode === "confirm-new-key") {
        setMasterKeyMode(`${pendingOperation}-key`);
        if (pendingOperation === OPERATION.CHANGE) setInvalidTextOldKey(message);
        else setInvalidText(message);
      } else if (masterKeyMode === "change-key") setInvalidTextOldKey(message);
      else setInvalidText(message);
    } finally {
      submittingRef.current = false;
    }
  };

  const changeMode = (mode) => {
    invalidateFlow();
    clearErrors();
    setPendingOperation(null);
    if (mode === "change-key") {
      setOldKey("");
      setKey("");
    } else if (["reset-key", "enter-key", "create-key"].includes(mode)) setKey("");
    setMasterKeyMode(mode);
  };

  const close = (withoutKey = false) => {
    if (withoutKey) finishWithoutKey();
    else finish(onClose);
  };

  return {
    masterKeyMode,
    key,
    oldKey,
    invalidText,
    invalidTextOldKey,
    setKey: (value) => {
      setKey(value);
      if (invalidText) setInvalidText("");
    },
    setOldKey: (value) => {
      setOldKey(value);
      if (invalidTextOldKey) setInvalidTextOldKey("");
    },
    setInvalidText,
    setInvalidTextOldKey,
    changeMode,
    resetInputs,
    submit,
    close,
  };
}
